import { motion } from "framer-motion";
import {
    Crown,
    Server,
    Rocket,
    Briefcase,
    PenTool,
    CheckCircle2,
} from "lucide-react";

const segments = [
    {
        icon: Crown,
        tag: "Founders & CEOs",
        title: "See the company without asking anyone.",
        desc: "Real-time visibility across revenue, people, projects and cash — without chasing reports.",
        wins: [
            "Five signals on one screen",
            "Decision cycles measured in hours, not weeks",
            "Time back to lead, not coordinate",
        ],
        accent: "text-electric",
        accentBg: "bg-electric/10 border-electric/25",
        ring: "from-electric/30 to-electric/0",
    },
    {
        icon: Server,
        tag: "IT Services Companies",
        title: "From bench utilisation to bottom line.",
        desc: "Connect delivery, sales and HR so every billable hour, sprint and milestone is visible end-to-end.",
        wins: [
            "Utilisation, capacity & margins in one view",
            "Project health tied to revenue forecast",
            "Standardised SOPs across delivery pods",
        ],
        accent: "text-cyan-300",
        accentBg: "bg-cyan-300/10 border-cyan-300/25",
        ring: "from-cyan-400/30 to-cyan-400/0",
    },
    {
        icon: Rocket,
        tag: "SaaS Startups",
        title: "Operate like a Series-B company on day one.",
        desc: "Get sales, support, HR and product ops out of spreadsheets without buying five separate tools.",
        wins: [
            "One source of truth from team #5 onwards",
            "Pipeline, churn and runway side by side",
            "Built-in automations replace fragile glue scripts",
        ],
        accent: "text-emerald-300",
        accentBg: "bg-emerald-300/10 border-emerald-300/25",
        ring: "from-emerald-400/30 to-emerald-400/0",
    },
    {
        icon: Briefcase,
        tag: "Consultancies",
        title: "Engagements that don't leak revenue.",
        desc: "Proposals, scopes, tasks, time and invoicing live on one ledger — no more reconciliations on Friday.",
        wins: [
            "Quote → engagement → invoice on one thread",
            "Margin per engagement, in real time",
            "Client-ready dashboards by default",
        ],
        accent: "text-amber-glow",
        accentBg: "bg-amber-300/10 border-amber-300/25",
        ring: "from-amber-400/30 to-amber-400/0",
    },
    {
        icon: PenTool,
        tag: "Digital Agencies",
        title: "Stop running the agency from Slack.",
        desc: "Briefs, tasks, approvals, retainers and team chat in one workspace — clients included, not chasing.",
        wins: [
            "Retainer burn tracked against work shipped",
            "Approvals live on the asset, not in DMs",
            "Client portals without extra tooling",
        ],
        accent: "text-rose-300",
        accentBg: "bg-rose-300/10 border-rose-300/25",
        ring: "from-rose-400/30 to-rose-400/0",
    },
    {
        icon: Crown,
        tag: "Operators",
        title: "Run the system that runs the company.",
        desc: "COOs and ops leaders get the lever they've always needed — one config surface, every workflow.",
        wins: [
            "Workflows you can edit without engineering",
            "Audit trails on every state change",
            "Plug-in playbooks for new functions",
        ],
        accent: "text-violet-300",
        accentBg: "bg-violet-300/10 border-violet-300/25",
        ring: "from-violet-400/30 to-violet-400/0",
    },
];

export const BuiltForWho = () => {
    return (
        <section
            id="built-for"
            className="relative py-24 sm:py-32 overflow-hidden"
            data-testid="built-for-section"
        >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div className="absolute inset-0 radial-glow opacity-40 pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-amber-glow font-semibold mb-4">
                            // built for the companies that need it most
                        </p>
                        <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05]">
                            Not a generic tool.
                            <br />
                            <span className="gradient-text-amber">
                                An operating layer for operators.
                            </span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-zinc-400 text-base sm:text-lg leading-relaxed font-light">
                        ApexBrain is built for teams that have outgrown spreadsheets
                        but haven't decided to live inside fifteen disconnected
                        SaaS tabs. Pick your reality below.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                    {segments.map((s, i) => (
                        <motion.div
                            key={s.tag}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: i * 0.06 }}
                            whileHover={{ y: -4 }}
                            className="relative h-full rounded-2xl border border-white/[0.06] bg-obsidian-200/70 backdrop-blur-sm p-7 overflow-hidden hover:border-white/15 transition-colors"
                            data-testid={`built-for-card-${i}`}
                        >
                            <div
                                className={`absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-to-br ${s.ring} blur-3xl pointer-events-none`}
                            />
                            <div className="relative flex items-center gap-3 mb-5">
                                <span
                                    className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border ${s.accentBg}`}
                                >
                                    <s.icon
                                        className={`w-4 h-4 ${s.accent}`}
                                    />
                                </span>
                                <span
                                    className={`text-[10px] font-code uppercase tracking-[0.2em] font-semibold ${s.accent}`}
                                >
                                    {s.tag}
                                </span>
                            </div>
                            <h3 className="relative font-outfit text-lg sm:text-xl font-semibold text-white leading-snug">
                                {s.title}
                            </h3>
                            <p className="relative mt-3 text-sm text-zinc-400 leading-relaxed font-light">
                                {s.desc}
                            </p>
                            <ul className="relative mt-6 space-y-2 pt-5 border-t border-white/[0.06]">
                                {s.wins.map((w) => (
                                    <li
                                        key={w}
                                        className="flex items-start gap-2.5 text-xs sm:text-[13px] text-zinc-300"
                                    >
                                        <CheckCircle2
                                            className={`shrink-0 w-3.5 h-3.5 mt-0.5 ${s.accent}`}
                                        />
                                        <span>{w}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="relative mt-6 flex items-center justify-between text-[10px] font-code text-zinc-600">
                                <span>
                                    segment_{String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse-glow" />
                                    live
                                </span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
